import * as React from "react";
import { api, ApiError, getSessionSignal } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInternalTransferNumbers } from "@/lib/queries";
import type {
  BlindTransfer,
  ConferenceRoom,
  RoomLegs,
  RoomParticipant,
  SoftphoneCredentials,
  WarmTransfer,
} from "@/lib/types";
import {
  campaignAnswering,
  incomingCallIdFrom,
  readAutoAnswer,
  selfAnswerFailed,
  shouldAutoAnswer,
  subscribeCampaignAnswering,
  writeAutoAnswer,
} from "@/softphone/auto-answer";
import {
  canDialSecondLine,
  heldFromModifyResponse,
  isEngaged,
  nextActiveCall,
  secondaryRinging,
  swapPlan,
  swappableCalls,
} from "@/softphone/call-control";
import {
  applyLegKinds,
  applyLiveArrayEvent,
  conferenceCallIdFrom,
  conferenceFromJoin,
  describeModFailure,
  isConferencePart,
  MEMBER_ACTION_LABELS,
  memberCommand,
  modResponseFrom,
  pvtDataFrom,
  unidentifiedChannelIds,
  type ConferenceParticipant,
  type ConferenceState,
  type LiveArrayPacket,
  type MemberAction,
} from "@/softphone/conference";
import { currentMonitors, monitorAsked, subscribeMonitors } from "@/lib/monitor";
import { normalizeDialString } from "@/lib/phone-number";
import { answerFailureMessage, answerWatchingTheCall } from "@/softphone/answer-failure";
import { micForCall, readAudioPrefs, subscribeAudioPrefs } from "@/softphone/audio-devices";
import { AnswerOnce } from "@/softphone/answer-once";
import { AutoplayBlocked, RemoteAudio, remoteStreamFrom } from "@/softphone/remote-audio";
import { sameNumber } from "@/lib/phone-number";
import { forgetRingerSetting, toneFor, Tones } from "@/softphone/tones";
import { SessionLock } from "@/softphone/session-lock";
import {
  describeDrop, socketDrops, surroundings, watchPage, type SocketClose,
} from "@/softphone/socket-drops";
import { clearTab, readTab, writeTab } from "@/softphone/tab-storage";
// Types only — the implementation is imported dynamically in `start()` below, so the ~60KB
// vendored Verto client is not in the initial bundle. Most page loads never place a call, and
// admins never have an endpoint at all.
import { markInternalTransfers, remoteName, remoteNumber } from "@/softphone/party";
import type { VertoCallInfo, VertoClient } from "@/softphone/vendor";

/**
 * The console's phone: one Verto (WebRTC) registration, driven by the endpoint assigned to the
 * signed-in user.
 *
 * The shape of this module is dictated by two things the vendored client does not handle.
 *
 * 1. **React state has to mirror the client's internal call map**, not replace it. The vendored
 *    client is the source of truth for a call; we subscribe to `callStateChange` and rebuild a
 *    snapshot. Trying to track calls ourselves and keep them in step is how you get a UI that
 *    says "on hold" for a call that ended.
 * 2. **Registration is exclusive per person** (see session-lock.ts), so this can be in a
 *    `standby` state that is not an error — another tab has the line.
 * 3. **A conference roster has to be asked for.** The switch pushes a `pvtEvent` naming an
 *    event channel when we join a room, and sends nothing else until we subscribe to it — see
 *    conference.ts.
 * 4. **Call recovery is the caller's job to arm.** The client re-attaches live calls after a
 *    dropped socket on its own, but only because it still has `sessid` in memory. Across a page
 *    reload that is gone, so this module persists it per tab and hands it back to `login()` —
 *    see `sessidFor` below.
 */

export type RegistrationState =
  | "idle"          // no endpoint assigned, or not started
  | "connecting"
  | "registered"
  | "standby"       // another tab holds the line
  | "failed";

export type CallState = "trying" | "ringing" | "active" | "held" | "ended";

export interface Call {
  id: string;
  direction: "inbound" | "outbound";
  /** E.164 or extension of the other party. */
  remote: string;
  remoteName: string | null;
  /** A caller on GoHighLevel's own line: a rep there sending a client on (`isInternalTransfer`). */
  internalTransfer?: boolean;
  state: CallState;
  /** unix ms, set when the call became active — the timer counts from here. */
  answeredAt: number | null;
  muted: boolean;
}

export interface SoftphoneValue {
  available: boolean;                 // an endpoint is assigned to this user
  registration: RegistrationState;
  error: string | null;
  credentials: SoftphoneCredentials | null;
  calls: Call[];
  primary: Call | null;
  /** The leg that owns the microphone and the speaker. Only this one is off hold. */
  activeCallId: string | null;
  /** A call ringing while the rep is already on one — the case that needs a decision. */
  waiting: Call | null;
  /** Live calls the rep could swap to (everything but the active one). */
  swappable: Call[];
  /** This browser picks inbound calls up on its own. Per device, not per account. */
  autoAnswer: boolean;
  setAutoAnswer: (enabled: boolean) => void;
  /** The room this user's phone was last sent to, for as long as the tab is open. */
  room: ConferenceRoom | null;
  /** Live roster of the conference this phone is in, or null when it is not in one. */
  conference: ConferenceState | null;
  /**
   * The last moderator command the switch refused, in a sentence, or null.
   *
   * Kept apart from `error` — which is about the phone's own registration — because it is about
   * one request the rep just made, and it survives only until they make another. It exists
   * because a refused conf-control command is otherwise completely silent: the broadcast that
   * carried it succeeds either way, and the roster simply does not change.
   */
  conferenceError: string | null;
  /**
   * Legs the console has asked the switch to hand over and is waiting to lose.
   *
   * Shared rather than kept per button because one hand-over can be offered from two places at
   * once: a warm transfer names two calls, and both of their rows carry the button. Keeping the
   * in-flight state on the button that was clicked would leave the other one live, and a second
   * click would ask the switch to bridge a pair it has already bridged.
   *
   * It clears when the legs actually go (the hand-off effect below), or immediately if the
   * request failed — never on the mere fact that the command was accepted, which is not the
   * same as the switch having carried it out.
   */
  handingOver: readonly string[];
  connect: () => Promise<void>;
  disconnect: () => void;
  takeOver: () => Promise<void>;      // steal the line from another tab
  dial: (destination: string) => Promise<void>;
  /** Ask core-api to ring this phone into the user's own conference room. */
  createRoom: () => Promise<ConferenceRoom>;
  /**
   * Ask core-api to ring this phone for a sound test (ADR-062 in `platform`), and resolve with
   * the id the call will ring under.
   *
   * The id is minted here and sent, not read off the response: mod_verto offers the call under
   * the channel's uuid, which is this id, and the call can ring before the response is back.
   * The phone answers it on its own, with no bell, whatever the auto-answer setting says — the
   * rep has just pressed the button — unless another call is up, which it never barges into.
   */
  startSoundTest: () => Promise<string>;
  /**
   * The calls that are this phone's own sound tests, still up or still coming. A view uses it to
   * leave out what does not apply to one: the switch never records a test, whatever the
   * account's recording toggle says.
   */
  soundTests: readonly string[];
  /**
   * Hand the party on the other end of `callId` to the room this phone is in.
   *
   * `callId` is the rep's own leg; core-api transfers its bridge partner. The leg then ends on
   * its own, and the room leg it was parked for is resumed — see the effect below.
   */
  addToRoom: (callId: string) => Promise<RoomParticipant>;
  /**
   * Introduce the two people this phone has on the line and step out of the call.
   *
   * Both ids are the rep's OWN legs; core-api resolves the far end of each and bridges those
   * two. Nothing here reports success, because there is none to report: the transfer *is* both
   * of these calls ending, which the switch does on its own once their parties are bridged
   * elsewhere. What resolving means is that the command reached the bus.
   */
  warmTransfer: (fromCallId: string, toCallId: string) => Promise<WarmTransfer>;
  /**
   * Send the caller on `callId` to `destination` and let go of them.
   *
   * `callId` is the rep's own leg; the switch transfers its bridge partner. The destination is
   * whatever the rep typed — an extension or a number — stripped of the punctuation a written
   * number carries (`lib/phone-number.ts`), and dialled in their own domain's context, which the
   * server supplies and the console cannot influence.
   *
   * Like the other two hand-overs this reports no success: the leg ending is the transfer.
   */
  blindTransfer: (callId: string, destination: string) => Promise<BlindTransfer>;
  /** `byItself`: the phone is picking the call up without the rep's click (the campaign's
   *  auto-answer), and says so with one ding (`Tones.ding`). A rep's own Answer makes no sound. */
  answer: (callId?: string, how?: { byItself?: boolean }) => Promise<void>;
  /** Park the current call and take the waiting one. */
  holdAndAnswer: (callId: string) => Promise<void>;
  /** Hang the current call up and take the waiting one. */
  endAndAnswer: (callId: string) => Promise<void>;
  /** Make `callId` the live leg, putting whoever was live on hold. */
  switchTo: (callId: string) => Promise<void>;
  hangup: (callId?: string) => Promise<void>;
  reject: (callId?: string) => Promise<void>;
  toggleHold: (callId?: string) => Promise<void>;
  toggleMute: (callId?: string) => Promise<void>;
  sendDigit: (digit: string, callId?: string) => Promise<void>;
  /**
   * A call this phone is about to answer itself as it arrives: the number the rep dialed by hand,
   * coming back through the ACD (ADR-047 in `platform`, `useOwnCallAutoAnswer`). While it is
   * set, a call ringing from that number gets no incoming-call bell — to the rep it is their own
   * call ringing out, and the card is already sounding the ringback for it. Null clears it.
   */
  expectCall: (number: string | null) => void;
  /**
   * Act on somebody else in the room: mute them, deaf them, send them somewhere, remove them.
   *
   * Resolving means the switch took the broadcast, which is not the same as it having carried
   * the command out — it answers that separately and asynchronously, and a refusal lands in
   * `conferenceError`. Success is not reported at all, because the roster reports it: the flag
   * this flipped comes back down the liveArray a moment later, which is the same thing the rep
   * is already watching.
   */
  memberCommand: (
    action: MemberAction,
    participant: ConferenceParticipant,
    value?: string,
  ) => Promise<void>;
}

/**
 * Exported for one consumer only: the dev-time state gallery (`routes/dev-phone.tsx`), which
 * renders the dock over a hand-built value so every state can be seen without a switch. The
 * app itself goes through `SoftphoneProvider`.
 */
export const SoftphoneContext = React.createContext<SoftphoneValue | null>(null);

function toCall(
  info: VertoCallInfo,
  muted: boolean,
  answeredAt: number | null,
  held: boolean,
): Call {
  // Which end of the caller profile is "the other party" is decided by direction alone — see
  // party.ts, which is also where the names the switch sends that are not names get dropped.
  const remote = remoteNumber(info);

  // `held` comes from us, not from the client: the vendored `toggleHold` sends verto.modify and
  // leaves its own `state` on "active" forever, so a UI mirroring that map can never show hold.
  const state: CallState = held
    ? "held"
    : info.state === "active" || info.state === "held" || info.state === "ringing" ||
        info.state === "trying" || info.state === "ended"
      ? info.state
      : "trying";

  return {
    id: info.callId,
    direction: info.direction,
    remote: remote || "unknown",
    remoteName: remoteName(info, remote),
    state,
    answeredAt,
    muted,
  };
}


/**
 * Bound how long the UI can sit in "connecting".
 *
 * The vendored client's `connect()` has a 10-second timeout that only fires while the socket is
 * still `CONNECTING`. A refused connection closes immediately, so that check never matches and
 * the promise settles neither way — the await hangs, `login()` is never reached, and the only
 * thing that eventually moves the UI is `connectionFailed` after the whole retry budget (about
 * 31 seconds). Half a minute of "Connecting…" with no way out is not a state to ship, and
 * fixing it inside the vendored file would put it out of step with tap-ucaas for the next port.
 */
function withTimeout<T>(
  promise: Promise<T>, ms: number, message: string, signal: AbortSignal,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(message));
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
    if (signal.aborted) onAbort();
    promise.then(
      (value) => {
        cleanup();
        resolve(value);
      },
      (error) => {
        cleanup();
        reject(error);
      },
    );
  });
}

const CONNECT_TIMEOUT_MS = 12_000;

/**
 * The Verto session id, remembered per tab.
 *
 * This is what makes call recovery work across a reload. `login()` takes an optional `sessid`;
 * hand it one FreeSWITCH still knows and the switch answers with `verto.attach` for every live
 * call on that session, which the vendored client turns back into a real call with audio. Log in
 * without one — what this module did until now — and the switch has no way to connect the new
 * page to the old session, so a rep who refreshed mid-call lost the call from their screen while
 * it was still very much up on the other end.
 *
 * Stored against the login it belongs to: replaying one account's sessid on another account's
 * login would ask the switch to hand this rep somebody else's calls. `sessionStorage`, so it
 * dies with the tab and never leaks to a second one (see tab-storage.ts).
 */
const SESSID_KEY = "acl.softphone.sessid";

function readSessid(login: string): string | undefined {
  const raw = readTab(SESSID_KEY);
  if (!raw) return undefined;
  try {
    const saved = JSON.parse(raw) as { login?: string; sessid?: string };
    return saved.login === login && saved.sessid ? saved.sessid : undefined;
  } catch {
    return undefined;
  }
}

function writeSessid(login: string, sessid: string | null): void {
  if (!sessid) return;
  writeTab(SESSID_KEY, JSON.stringify({ login, sessid }));
}

/**
 * How long to wait before connecting on our own.
 *
 * Not for the network's sake — for React's. StrictMode mounts, unmounts and remounts in
 * development, and a connection opened in the first pass is torn down by the simulated unmount
 * with nothing left to retry it. Scheduling the attempt and cancelling it in the effect's own
 * cleanup means only the surviving pass connects, in dev and in production alike.
 */
const AUTO_CONNECT_DELAY_MS = 150;

/**
 * What to say when the WebSocket will not open.
 *
 * The browser refuses to tell JavaScript why a `wss://` handshake failed — an untrusted
 * certificate, a closed port and a wrong hostname are indistinguishable from here. Since a
 * self-signed certificate on a fresh FreeSWITCH node is far and away the most common cause,
 * and its fix (open the URL once and accept it) is not guessable, name it.
 */
function unreachableMessage(wsUrl: string): string {
  return (
    `Could not reach the switch at ${wsUrl}. Check that mod_verto is listening there, and — ` +
    `if it uses a self-signed certificate — open ${wsUrl.replace(/^wss:/, "https:")} once in ` +
    `this browser and accept it.`
  );
}

export function SoftphoneProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const sessionSignal = getSessionSignal();
  const [registration, setRegistration] = React.useState<RegistrationState>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [credentials, setCredentials] = React.useState<SoftphoneCredentials | null>(null);
  const [liveCalls, setCalls] = React.useState<Call[]>([]);
  // A call from GoHighLevel's own line reads as an internal transfer on every screen that names
  // it (`describeParty`), so the mark goes on the call list itself, once.
  const { data: transferNumbers } = useInternalTransferNumbers({
    enabled: Boolean(user?.sip_endpoint_id) && !user?.signed_in_by,
  });
  const calls = React.useMemo(
    () => markInternalTransfers(liveCalls, transferNumbers),
    [liveCalls, transferNumbers],
  );
  const [autoAnswer, setAutoAnswerState] = React.useState<boolean>(readAutoAnswer);
  // The phone always rings (`tones.ts`): a browser that once switched its ringer off must not
  // stay silent, so the retired setting is cleared rather than left to confuse anybody.
  React.useEffect(() => forgetRingerSetting(), []);
  // The number of a call the phone will answer on its own the moment it rings (`expectCall`).
  const [expectedNumber, setExpectedNumber] = React.useState<string | null>(null);
  // For the `incomingCall` handler, which is registered once per connection (as `autoAnswerRef`).
  const expectedNumberRef = React.useRef(expectedNumber);
  expectedNumberRef.current = expectedNumber;
  /**
   * Calls the far end has sent media for.
   *
   * Kept apart from `RemoteAudio`'s own map, which is a cache and not a signal: this drives a
   * render (the ringback has to stop the instant early media arrives), and reaching into the
   * audio object from an effect would make that a poll.
   */
  const [withMedia, setWithMedia] = React.useState<ReadonlySet<string>>(() => new Set());
  const [room, setRoom] = React.useState<ConferenceRoom | null>(null);
  const [conference, setConference] = React.useState<ConferenceState | null>(null);
  const [conferenceError, setConferenceError] = React.useState<string | null>(null);

  const clientRef = React.useRef<VertoClient | null>(null);
  const lockRef = React.useRef<SessionLock | null>(null);
  // Owns the one <audio> element the far end plays through; see remote-audio.ts.
  const audioRef = React.useRef<RemoteAudio | null>(null);
  // The ring and the ringback. Made once and kept for the life of the provider: an AudioContext
  // per call would hit the autoplay policy afresh every time, and after the first few the
  // browser starts refusing.
  const tonesRef = React.useRef<Tones | null>(null);
  // Per-call bookkeeping the vendored client does not track for us.
  const answeredAtRef = React.useRef<Map<string, number>>(new Map());
  const mutedRef = React.useRef<Set<string>>(new Set());
  // One answer per call at a time: a second one waits on the first (`answer-once.ts`).
  const answersRef = React.useRef(new AnswerOnce());
  // Hold is ours to remember. The switch knows, the vendored client does not — see call-control.
  const heldRef = React.useRef<Set<string>>(new Set());
  // Which leg owns the microphone and the speaker. Kept in a ref as well as state because
  // `syncCalls` runs from vendored-client events, outside React's render cycle.
  const activeCallIdRef = React.useRef<string | null>(null);
  const [activeCallId, setActiveCallId] = React.useState<string | null>(null);
  // Distinguishes "never got connected" from "was connected and dropped" — the two need
  // different messages, and only the second should say "reconnecting".
  const registeredOnce = React.useRef(false);
  // The `incomingCall` handler is registered once per connection and closes over whatever
  // `autoAnswer` was at that moment, so it reads the setting through a ref instead. Toggling
  // the switch has to take effect on the very next call, not on the next re-registration.
  const autoAnswerRef = React.useRef(autoAnswer);
  autoAnswerRef.current = autoAnswer;
  // Sound tests this phone asked for and answers itself when they ring (`startSoundTest`). Kept
  // twice: the ref for the `incomingCall` handler, which is registered once per connection and
  // would read a stale render's state; the state for views (`soundTests`).
  const soundTestsRef = React.useRef<Set<string>>(new Set());
  const [soundTests, setSoundTests] = React.useState<readonly string[]>([]);
  const forgetSoundTest = React.useCallback((callId: string) => {
    if (!soundTestsRef.current.delete(callId)) return;
    setSoundTests([...soundTestsRef.current]);
  }, []);
  // A connect already under way. `connect()` is reachable from the button, the dock and the
  // automatic attempt below, and two of them overlapping would build two Verto clients — the
  // second wins the ref and the first is left holding an open socket nobody disconnects.
  const connectingRef = React.useRef(false);
  const mountedRef = React.useRef(false);
  // Also cancel pre-client work (credentials/import), which a socket ref cannot identify yet.
  const attemptRef = React.useRef<AbortController | null>(null);
  // The rep pressed Disconnect. Their phone stays off until they say otherwise; re-connecting
  // it for them a moment later is the one thing "Disconnect" must not do.
  const stayDisconnectedRef = React.useRef(false);
  // The liveArray subscription handle, so leaving a room unsubscribes rather than leaving a
  // dead handler wired to the next room's channel.
  const laSubRef = React.useRef<unknown>(null);
  // The same, for the moderator channel: commands go out on it and the switch answers on it, so
  // it is subscribed for the answers rather than for anything it pushes unasked.
  const modSubRef = React.useRef<unknown>(null);
  // What the rep last asked for, in their words, so the switch's refusal can be introduced by
  // the action it refused. Overwritten per command and read once — a second command before the
  // first was answered costs the wrong verb in a message, never the wrong command on the wire.
  const lastMemberActionRef = React.useRef<string>("The command");
  // The room, for `sendMemberCommand`. It is on the context value, whose identity every consumer
  // depends on, so reading the room through a ref keeps that value from being rebuilt — and
  // every button on the dock from re-rendering — each time somebody in the room starts talking.
  const conferenceRef = React.useRef<ConferenceState | null>(null);
  conferenceRef.current = conference;
  // Legs the rep has just given away, and the leg that has to come off hold once they have all
  // gone. Both hand-offs use it: "move into my room" gives away one leg, a warm transfer gives
  // away two. See the effect below — the wait is the whole point, so it cannot be a plain await.
  const handoffRef = React.useRef<{ moved: string[]; resume: string | null } | null>(null);
  // The same list, as state, because the buttons offering the hand-over have to go quiet while
  // it is in flight. The ref is what the effect below reads; this is what the UI reads.
  const [handingOver, setHandingOver] = React.useState<readonly string[]>([]);

  /** Record a hand-over in flight. Both halves move together, so they are set together. */
  const markHandoff = React.useCallback((moved: string[], resume: string | null) => {
    handoffRef.current = { moved, resume };
    setHandingOver(moved);
  }, []);

  /** Undo it — the request failed, so the rep still has every leg they had. */
  const clearHandoff = React.useCallback(() => {
    handoffRef.current = null;
    setHandingOver([]);
  }, []);

  const setAutoAnswer = React.useCallback((enabled: boolean) => {
    setAutoAnswerState(enabled);
    writeAutoAnswer(enabled);
  }, []);

  // No phone for a session an admin opened as this person (ADR-040 in `platform`): their calls do
  // not ring here, and core-api refuses the credentials anyway.
  const available = Boolean(user?.sip_endpoint_id) && !user?.signed_in_by;

  const syncCalls = React.useCallback(() => {
    const client = clientRef.current;
    if (!client) {
      activeCallIdRef.current = null;
      setActiveCallId(null);
      return setCalls([]);
    }
    const live = client.getAllCalls().filter((c) => c.state !== "ended");
    const calls = live.map((c) =>
      toCall(
        c,
        mutedRef.current.has(c.callId),
        answeredAtRef.current.get(c.callId) ?? null,
        heldRef.current.has(c.callId),
      ),
    );
    // A call that ended takes its bookkeeping with it, or a later call reusing nothing but the
    // same shape inherits a stale "held" and shows as parked the moment it connects.
    const liveIds = new Set(calls.map((c) => c.id));
    for (const id of [...heldRef.current]) if (!liveIds.has(id)) heldRef.current.delete(id);
    for (const id of [...mutedRef.current]) if (!liveIds.has(id)) mutedRef.current.delete(id);
    audioRef.current?.retain(liveIds);
    // The same rule for the media flags: without it a rep who redials after a failed call gets
    // no ringback, because a set that never forgets still names a leg that ended.
    setWithMedia((prev) => {
      const kept = [...prev].filter((id) => liveIds.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });

    const nextActive = nextActiveCall(calls, activeCallIdRef.current, heldRef.current);
    if (nextActive !== activeCallIdRef.current) {
      activeCallIdRef.current = nextActive;
      setActiveCallId(nextActive);
      void audioRef.current?.route(nextActive);
    }
    setCalls(calls);
  }, []);

  // The current call list, readable from callbacks that are not re-created per render.
  const callsRef = React.useRef<Call[]>([]);
  callsRef.current = calls;

  const setActive = React.useCallback((callId: string | null) => {
    activeCallIdRef.current = callId;
    setActiveCallId(callId);
    void audioRef.current?.route(callId);
  }, []);

  /** Hold or resume one leg, recording only the switch's answer, never an optimistic state. */
  const holdCall = React.useCallback(async (
    client: VertoClient, callId: string, action: "hold" | "toggleHold" = "toggleHold",
  ) => {
    const response = await client[action](callId);
    const held = heldFromModifyResponse(response);
    if (action === "hold" && (!held || (response as { callID?: unknown }).callID !== callId)) {
      throw new Error("The switch did not confirm hold for this call");
    }
    if (held) heldRef.current.add(callId);
    else heldRef.current.delete(callId);
    return held;
  }, []);

  /**
   * Which microphone this call captures from, and a warning when it is not the one the rep chose.
   *
   * Every dial and every answer goes through here, because the answer can change between two
   * calls: a rep picks their USB headset in the morning and takes it home at lunchtime, and
   * `micForCall` notices the device has gone and puts the call on the system default rather than
   * letting `getUserMedia` reject it (`audio-devices.ts` has the reasoning). The rep is told,
   * since a call that sounds wrong is otherwise a mystery they hear about from the lead.
   */
  const micFor = React.useCallback(async () => {
    try {
      const decided = await micForCall();
      if (decided.fallback) {
        setError(
          "The microphone you chose is not connected, so this call is using the system default.",
        );
      }
      return decided;
    } catch (e) {
      // Never a reason not to place a call: the browser's own default is what the phone used
      // before any of this existed.
      console.error("[softphone] could not resolve the microphone", e);
      return { audio: { channelCount: 1 }, deviceId: undefined, fallback: false };
    }
  }, []);

  /**
   * One ding: the phone is picking a call up by itself (`Tones.ding`). Called at the two places
   * that happens with nobody having asked for the call a moment ago: this browser's auto-answer,
   * in the `incomingCall` handler, and the campaign's, through `answer(id, { byItself: true })`.
   */
  const ding = React.useCallback(() => {
    const tones = (tonesRef.current ??= new Tones());
    tones.setSink(readAudioPrefs().speakerId);
    tones.ding();
  }, []);

  /**
   * Answer `callId` with the microphone `micFor` picks: the one place the vendored client's
   * `answer` is called. A second answer of a call still being answered waits on the first instead
   * of opening a second peer connection, which left the call connected and silent (`AnswerOnce`).
   */
  const answerCall = React.useCallback(
    (client: VertoClient, callId: string) =>
      answersRef.current.run(callId, () =>
        answerWatchingTheCall(client, callId, async () => {
          await client.answer(callId, { audio: (await micFor()).audio });
        }),
      ),
    [micFor],
  );

  /**
   * Answer `callId`, parking whoever the rep is already talking to.
   *
   * Every route to answering goes through here — the plain Answer button included. It used to
   * be a bare `client.answer(id)`, which is how a second call could still end up connected
   * alongside the first: the dock only offers the hold-or-hangup choice while the current leg
   * is *engaged*, so a call answered while another was still dialling took the plain path and
   * held nothing.
   *
   * Both entry points fail closed: a failed hold leaves the incoming call ringing and does
   * not change the active call or its audio route. The warning offers explicit recovery.
   */
  const answerLeg = React.useCallback(
    async (client: VertoClient, callId: string, onHoldFailure: (e: unknown) => void) => {
      const outgoing = activeCallIdRef.current;
      const other = outgoing ? callsRef.current.find((c) => c.id === outgoing) : undefined;
      // `verto.modify` on a leg that is still dialling is an error, so only an engaged call is
      // worth holding — and one already held must not be toggled back off.
      if (other && other.id !== callId && isEngaged(other.state) && !heldRef.current.has(other.id)) {
        try {
          // A timed-out request may have reached the switch. Explicit hold is safe to retry;
          // toggleHold could undo that successful hold when its acknowledgement was lost.
          await holdCall(client, other.id, "hold");
        } catch (e) {
          console.error("[softphone] could not hold before answering", e);
          onHoldFailure(e);
          return;
        }
      }
      // Promote BEFORE the round trip: `client.answer` emits `callStateChange "active"` while
      // we are still awaiting it, and syncCalls must already know which leg owns the audio or
      // the newly answered call arrives with its stream routed to the old one.
      setError(null);
      setActive(callId);
      await answerCall(client, callId);
      syncCalls();
    },
    [answerCall, holdCall, setActive, syncCalls],
  );

  const teardown = React.useCallback(() => {
    attemptRef.current?.abort();
    attemptRef.current = null;
    connectingRef.current = false;
    const client = clientRef.current;
    clientRef.current = null;
    // disconnect() can emit synchronously; retire the client before any of its callbacks run.
    client?.disconnect();
    audioRef.current?.dispose();
    audioRef.current = null;
    tonesRef.current?.stop();
    setWithMedia(new Set());
    lockRef.current?.release();
    lockRef.current = null;
    answeredAtRef.current.clear();
    mutedRef.current.clear();
    heldRef.current.clear();
    activeCallIdRef.current = null;
    setActiveCallId(null);
    handoffRef.current = null;
    setHandingOver([]);
    registeredOnce.current = false;
    setCalls([]);
    setRoom(null);
    setConference(null);
    setConferenceError(null);
    // The socket they were made on has just gone, so there is nothing to unsubscribe from —
    // only stale handles to drop, so the next room's subscriptions are the ones held here.
    laSubRef.current = null;
    modSubRef.current = null;
  }, []);

  const start = React.useCallback(
    async (creds: SoftphoneCredentials, steal: boolean, signal: AbortSignal) => {
      if (signal.aborted) return;
      const lock = lockRef.current ?? new SessionLock();
      lockRef.current = lock;

      const onLost = () => {
        teardown();
        setRegistration("standby");
      };
      if (steal) lock.steal(onLost);
      else if (!lock.acquire(onLost)) {
        setRegistration("standby");
        return;
      }

      setRegistration("connecting");
      setError(null);

      const { VertoClient } = await import("@/softphone/vendor");
      if (signal.aborted) return;
      const client = new VertoClient();
      clientRef.current = client;

      /**
       * Ignore events from a client we have already replaced.
       *
       * A torn-down client keeps its retry timers for a while — `disconnect()` stops it starting
       * new ones but cannot unwind a backoff already scheduled — so an old instance can emit
       * `connectionFailed` seconds after a new one has successfully registered, and its handlers
       * still close over the same setters. Without this guard, reconnecting to a corrected
       * PHONE_WS_URL shows "Registered" and then flips to "Disconnected" on its own, which is
       * exactly the kind of intermittent behaviour nobody can reproduce on demand.
       */
      const isCurrent = () => !signal.aborted && clientRef.current === client;

      const audio = (audioRef.current ??= new RemoteAudio());
      audio.setSink(readAudioPrefs().speakerId);
      client.iceServers = creds.ice_servers.map((s) => ({
        urls: s.urls,
        ...(s.username ? { username: s.username } : {}),
        ...(s.credential ? { credential: s.credential } : {}),
      })) as RTCIceServer[];

      client.on("remoteStream", (...args: unknown[]) => {
        if (!isCurrent()) return;
        const stream = remoteStreamFrom(args);
        if (!stream) {
          // Only reachable if the vendored client's event payload changes shape. Loud, because
          // the symptom otherwise is a call that connects perfectly and is silent.
          console.error("[softphone] remoteStream event carried no usable stream", args);
          setError("The call connected but no audio stream arrived. Please report this.");
          return;
        }
        const callId = (args[0] as { callId?: string } | undefined)?.callId ?? "";
        // Offered, not played: with two calls up, only the active leg's stream may reach the one
        // audio element. Playing every stream that arrives is what made a second call replace the
        // first in the rep's ear while the first was still connected.
        // Before the play attempt, not after: this is what silences the local ringback, and the
        // far end is already talking (or announcing) whether or not our own sink starts.
        if (callId) setWithMedia((prev) => (prev.has(callId) ? prev : new Set(prev).add(callId)));
        audio.offer(callId, stream).catch((e: unknown) => {
          if (!isCurrent()) return;
          if (e instanceof AutoplayBlocked) setError(e.message);
          else setError("Could not start audio playback.");
        });
      });

      client.on("callStateChange", (...args: unknown[]) => {
        if (!isCurrent()) return;
        const info = args[0] as VertoCallInfo | undefined;
        if (info?.state === "active" && !answeredAtRef.current.has(info.callId)) {
          answeredAtRef.current.set(info.callId, Date.now());
        }
        syncCalls();
      });
      client.on("incomingCall", (...args: unknown[]) => {
        if (!isCurrent()) return;
        syncCalls();
        const callId = incomingCallIdFrom(args);
        if (!callId) return;
        // Decided against the CLIENT's call map, not against React state: `calls` here is a
        // render-old snapshot and would not yet contain this call at all.
        const snapshot = client.getAllCalls().map((c) => ({
          id: c.callId,
          direction: c.direction,
          state: c.state,
        }));
        const asked = autoAnswerRef.current || soundTestsRef.current.has(callId);
        if (!shouldAutoAnswer(asked, callId, snapshot)) return;
        // One ding as the phone picks a call up for this browser's auto-answer: such a call
        // never rings, and the ding is how the rep knows somebody is about to be in their ear.
        // Not for a call the rep asked for a moment ago, which is answered the same way: a
        // sound test, a number they dialed by hand coming back, or a supervisor's listen-in
        // (the client asked for no ring and no ding on that one, 2026-09-25).
        const arriving = client.getAllCalls().find((c) => c.callId === callId);
        const askedFor =
          soundTestsRef.current.has(callId) ||
          currentMonitors().has(callId) ||
          monitorAsked() ||
          (expectedNumberRef.current !== null &&
            arriving !== undefined &&
            sameNumber(toCall(arriving, false, null, false).remote, expectedNumberRef.current));
        if (autoAnswerRef.current && !askedFor) ding();
        void answerCall(client, callId)
          .then(() => { if (isCurrent()) syncCalls(); })
          .catch((e: unknown) => {
            if (!isCurrent()) return;
            selfAnswerFailed(callId);
            console.error("[softphone] auto-answer failed", e);
            const message = answerFailureMessage(e);
            if (message) setError(message);
          });
      });
      client.on("callEnded", (...args: unknown[]) => {
        if (!isCurrent()) return;
        const info = args[0] as VertoCallInfo | undefined;
        if (info) {
          answeredAtRef.current.delete(info.callId);
          mutedRef.current.delete(info.callId);
          forgetSoundTest(info.callId);
        }
        syncCalls();
      });
      // The vendored client retries a dropped socket five times with exponential backoff
      // before giving up, so `disconnected` is NOT a terminal state — reporting it as one
      // showed "Disconnected" while a reconnect was still in flight, and left the user with no
      // reason and nothing to do. Only `connectionFailed` (retries exhausted) is terminal.
      client.on("disconnected", (...args: unknown[]) => {
        if (!isCurrent()) return;
        // A close nobody here asked for (a disconnect we make retires the client first, so it
        // never gets this far): tell the log what the page knew. See socket-drops.ts.
        socketDrops.report(describeDrop((args[0] ?? {}) as SocketClose, surroundings()));
        if (registeredOnce.current) {
          setRegistration("connecting");
          setError("Lost the connection to the switch. Reconnecting…");
        }
      });
      client.on("connectionFailed", () => {
        if (!isCurrent()) return;
        setRegistration("failed");
        setError(unreachableMessage(creds.ws_url));
      });
      // The other half of `disconnected`. The vendored client reconnects and re-logs-in behind
      // our back, and until this handler existed nothing told the UI it had succeeded: the phone
      // was working again while the console still said "Connecting…" under a red banner, which
      // is indistinguishable from broken.
      const recovered = () => {
        if (!isCurrent()) return;
        writeSessid(creds.login, client.sessid);
        setRegistration("registered");
        setError(null);
        syncCalls();
        // Anything that could not be reported while the line was down goes now.
        void socketDrops.flush();
      };
      client.on("sessionRecovered", recovered);
      client.on("authenticated", recovered);
      client.on("callRecovered", (...args: unknown[]) => {
        if (!isCurrent()) return;
        const info = args[0] as VertoCallInfo | undefined;
        if (!info) return;
        // A re-attached call has been up since before this page existed; the switch does not
        // tell us since when, so the timer counts from the re-attach and is honest about being
        // a lower bound rather than inventing a start time.
        answeredAtRef.current.set(info.callId, info.startTime ?? Date.now());
        syncCalls();
      });
      /**
       * Joining a room is announced, not asked about — but the roster is not sent with it.
       *
       * The switch pushes `conference-liveArray-join` naming an event channel, and then says
       * nothing further until we subscribe to that channel. This handler is the missing
       * subscribe: without it the console sits in a room full of people and shows an empty
       * list, which looks exactly like a room nobody joined.
       */
      const dropRoster = () => {
        for (const ref of [laSubRef, modSubRef]) {
          const binding = ref.current;
          ref.current = null;
          if (binding) void client.unsubscribe(binding).catch(() => undefined);
        }
        setConference(null);
        setConferenceError(null);
      };
      client.on("pvtEvent", (...args: unknown[]) => {
        if (!isCurrent()) return;
        const pvt = pvtDataFrom(args);
        if (!pvt) return;
        if (isConferencePart(pvt)) {
          dropRoster();
          return;
        }
        // The leg the switch addressed this to, falling back to the one we know we just
        // answered: `activeCallIdRef` is the room leg at this moment because `answerLeg`
        // promotes before it answers.
        const joined = conferenceFromJoin(
          pvt,
          conferenceCallIdFrom(args) ?? activeCallIdRef.current,
        );
        if (!joined) return;   // a screen-share or video-only sub-event; not ours
        dropRoster();          // a fresh room supersedes whatever we were watching
        setConference(joined);
        void client
          .subscribe(joined.channel, {
            // The docs pass the dialog's callID here. FreeSWITCH does not require it for an
            // audio room, but it is what a moderator command is later authorised against. It is
            // the leg the join event named, or failing that the one we know we just answered —
            // never a scan, which with two calls up can pick the wrong one and can never pick a
            // held one.
            subParams: { callID: joined.callId },
            handler: (data: unknown) => {
              if (!isCurrent()) return;
              setConference((current) =>
                current ? applyLiveArrayEvent(current, data as LiveArrayPacket) : current,
              );
            },
          })
          .then((binding) => {
            if (!isCurrent()) return;
            laSubRef.current = binding;
            /**
             * Ask for the list as it stands. Subscribing alone brings only what changes from
             * here on: `add` as people join, `modify` as they start and stop talking, `del` as
             * they leave. A phone that joins a room with people already in it would list only
             * those who happen to speak or move after it arrived, and a silent member would
             * never appear (seen live on 2026-09-16: a colleague brought into a call saw the
             * caller, who was talking, and not the rep who had asked for them). mod_verto sends
             * the `bootObj` packet in reply to this command on the same channel, which is what
             * verto.js's own liveArray does right after it subscribes.
             */
            void client
              .broadcast(joined.channel, {
                liveArray: { command: "bootstrap", context: joined.channel, name: joined.name },
              })
              .catch((e: unknown) => {
                if (!isCurrent()) return;
                console.error("[softphone] liveArray bootstrap failed", e);
              });
          })
          .catch((e: unknown) => {
            if (!isCurrent()) return;
            // Not fatal to the call — the audio is already up. The roster is simply unavailable,
            // and saying so beats an empty list that looks like an empty room.
            console.error("[softphone] liveArray subscribe failed", e);
            setConference((current) => (current ? { ...current, participants: [] } : current));
          });

        /**
         * The moderator channel, when the switch has said we may use one.
         *
         * Subscribing is not what grants permission — `modChannel` being in the join message is
         * (see conference.ts) — and it is not needed to *send* either: `verto.broadcast` reaches
         * mod_conference regardless. It is subscribed to hear the answer. Every conf-control
         * command is replied to on this same channel, and that reply is the only place a
         * refusal appears, so without this a command the switch threw away and one it carried
         * out are the same event from here: nothing.
         */
        if (joined.modChannel) {
          void client
            .subscribe(joined.modChannel, {
              // No `subParams` here, unlike the liveArray above: mod_conference grants this
              // channel to the session's own event-channel cookie when it hands out the
              // moderator flag, so there is no leg to name and verto.js names none either.
              handler: (data: unknown) => {
                if (!isCurrent()) return;
                const response = modResponseFrom(data);
                if (!response) return;   // a broadcast from another moderator, not an answer
                setConferenceError(
                  response.ok ? null : describeModFailure(response, lastMemberActionRef.current),
                );
              },
            })
            .then((binding) => {
              if (!isCurrent()) return;
              modSubRef.current = binding;
            })
            .catch((e: unknown) => {
              if (!isCurrent()) return;
              // Commands will still go out and still work; only the report on them is lost. Not
              // worth a banner over a room that is otherwise fine, but worth saying out loud
              // when the next command appears to do nothing.
              console.error("[softphone] moderator channel subscribe failed", e);
            });
        }
      });
      client.on("callRecoveryFailed", (...args: unknown[]) => {
        if (!isCurrent()) return;
        const info = args[0] as { error?: string } | undefined;
        console.error("[softphone] call recovery failed", info);
        setError(
          "A call was still up on the switch but could not be re-attached to this page. " +
            "It may still be connected on the other end.",
        );
        syncCalls();
      });

      try {
        await withTimeout(
          (async () => {
            await client.connect(creds.ws_url);
            if (!isCurrent()) return;
            // The sessid from this tab's previous page load, when there is one: that is the
            // whole of what re-attaching a live call across a reload takes.
            await client.login(creds.login, creds.password, readSessid(creds.login) ?? null);
          })(),
          CONNECT_TIMEOUT_MS,
          "timeout",
          signal,
        );
        // Whatever the switch settled on — it may hand back a different id than we asked for.
        if (!isCurrent()) return;
        writeSessid(creds.login, client.sessid);
        registeredOnce.current = true;
        setRegistration("registered");
        setError(null);
      } catch (e) {
        if (!isCurrent()) return;
        // Stop the client retrying in the background, and drop the reference so `isCurrent()`
        // starts rejecting its events straight away rather than only once something replaces it.
        if (clientRef.current === client) clientRef.current = null;
        client.disconnect();
        setRegistration("failed");
        // `connect` rejects only on its own 10-second timeout, and the browser deliberately
        // hides *why* a WebSocket handshake failed (an untrusted certificate looks exactly
        // like an unreachable host). So name the causes rather than repeat "Connection
        // timeout", which tells nobody anything actionable.
        setError(
          e instanceof Error && e.message && !/timeout/i.test(e.message)
            ? e.message
            : unreachableMessage(creds.ws_url),
        );
      }
    },
    [answerCall, forgetSoundTest, syncCalls, teardown],
  );

  const connect = React.useCallback(async () => {
    if (!available || connectingRef.current || !mountedRef.current || sessionSignal.aborted) return;
    const attempt = new AbortController();
    attemptRef.current = attempt;
    const signal = AbortSignal.any([getSessionSignal(), attempt.signal]);
    connectingRef.current = true;
    stayDisconnectedRef.current = false;
    setError(null);
    try {
      const creds = await api.get<SoftphoneCredentials>("/auth/me/softphone");
      if (signal.aborted) return;
      setCredentials(creds);
      await start(creds, false, signal);
    } catch (e) {
      if (signal.aborted) return;
      setRegistration("failed");
      setError(
        e instanceof ApiError ? e.message : "Could not load the phone's credentials.",
      );
    } finally {
      if (attemptRef.current === attempt) connectingRef.current = false;
    }
  }, [available, sessionSignal, start]);

  const takeOver = React.useCallback(async () => {
    if (!available || connectingRef.current || !mountedRef.current || sessionSignal.aborted) return;
    const attempt = new AbortController();
    attemptRef.current = attempt;
    const signal = AbortSignal.any([getSessionSignal(), attempt.signal]);
    connectingRef.current = true;
    stayDisconnectedRef.current = false;
    try {
      const creds = credentials ?? (await api.get<SoftphoneCredentials>("/auth/me/softphone"));
      if (signal.aborted) return;
      setCredentials(creds);
      await start(creds, true, signal);
    } catch (e) {
      if (!signal.aborted) throw e;
    } finally {
      if (attemptRef.current === attempt) connectingRef.current = false;
    }
  }, [available, credentials, sessionSignal, start]);

  const disconnect = React.useCallback(() => {
    stayDisconnectedRef.current = true;
    // Hanging up the line on purpose ends the session for good: keeping the sessid would have
    // the next page load ask the switch to re-attach calls the rep deliberately walked away
    // from. A dropped socket, which is what recovery is actually for, never comes through here.
    clearTab(SESSID_KEY);
    teardown();
    setRegistration("idle");
  }, [teardown]);

  /**
   * Open (or re-join) this user's conference room.
   *
   * The console does not place this call — core-api does, by publishing an originate that rings
   * this user's endpoint and joins the answered leg to `<extension>-room`. It has to work that
   * way round: the room has to survive the rep hanging up their own leg and re-joining, and a
   * browser-placed `call("1001-room")` would need a dialplan entry per room to get there.
   *
   * The leg comes back as an ordinary inbound call, so it appears in the dock like any other —
   * and is picked up on its own when auto-answer is on.
   */
  const createRoom = React.useCallback(async (): Promise<ConferenceRoom> => {
    const created = await api.post<ConferenceRoom>("/phone/room");
    setRoom(created);
    return created;
  }, []);

  const startSoundTest = React.useCallback(async (): Promise<string> => {
    const callId = crypto.randomUUID();
    // Before the request, because the call can ring before the request's answer is back.
    soundTestsRef.current.add(callId);
    setSoundTests([...soundTestsRef.current]);
    try {
      await api.post("/phone/sound-test", { call_id: callId });
    } catch (e) {
      forgetSoundTest(callId);
      throw e;
    }
    return callId;
  }, [forgetSoundTest]);

  /**
   * Send one moderator command about one member of the room.
   *
   * This is the only thing in the phone that talks to the switch over `verto.broadcast` rather
   * than a JSON-RPC call of its own, and the difference matters: a broadcast is a message onto a
   * channel, so it succeeds as soon as mod_verto accepts it and says nothing about what
   * mod_conference then did with it. Everything this can check before that point, it checks
   * here and throws for — a phone that is not a moderator, a command that cannot be addressed —
   * because those are the refusals that would otherwise arrive as silence.
   *
   * Beyond it, the answer comes back on the moderator channel; see the subscription in `start`.
   */
  const sendMemberCommand = React.useCallback(
    async (action: MemberAction, participant: ConferenceParticipant, value?: string) => {
      const client = clientRef.current;
      const room = conferenceRef.current;
      if (!client) throw new Error("The phone is not connected.");
      if (!room?.modChannel) {
        throw new Error("You are not this room's host, so you cannot act on the people in it.");
      }
      const payload = memberCommand(action, participant, value);
      if (!payload) {
        throw new Error(
          action === "transfer"
            ? "Enter a number or extension to send them to."
            : "The switch has not given this person an id that can be addressed.",
        );
      }
      // Cleared rather than left standing: the rep has moved on, and a refusal from the previous
      // command sitting under a fresh one reads as this one having failed too.
      setConferenceError(null);
      lastMemberActionRef.current = MEMBER_ACTION_LABELS[action];
      await client.broadcast(room.modChannel, payload);
    },
    [],
  );

  /**
   * Ask core-api what the rows of the roster actually are.
   *
   * The switch sends a member list, not a call: three names, no indication of which one is the
   * potential client and which is the colleague who was just brought in. core-api can say,
   * because every leg carries that on the switch and the roster's hash keys are the legs'
   * channel uuids — see `softphone/conference.ts` and core-api's `src/legs.py`.
   *
   * Keyed on the *pending* ids rather than on the roster, which changes several times a second
   * while anybody is talking. That also settles the retry question: an answer, including
   * `unknown`, empties this list and stops the asking; a lookup that fails or goes unanswered
   * leaves it exactly as it was, and this effect does not re-run until the room's membership
   * changes. Deliberate — labels are worth a request when somebody joins, and not worth a
   * request loop for the rest of a call because the index was down once.
   */
  const pendingLegs = (conference ? unidentifiedChannelIds(conference) : []).join(",");
  React.useEffect(() => {
    if (!pendingLegs) return;
    void api
      .post<RoomLegs>("/phone/room/legs", { channel_ids: pendingLegs.split(",") })
      .then((answer) => {
        // Applied against whatever the roster is *now*: an answer about somebody who has since
        // left matches no row and is dropped, so this needs no cancellation.
        setConference((current) => (current ? applyLegKinds(current, answer.legs) : current));
      })
      .catch((e: unknown) => {
        // Not worth telling the rep about — they are on a call, and the roster is still there
        // with everything the switch sent. The badges are what is missing.
        console.warn("[softphone] could not identify the conference legs", e);
      });
  }, [pendingLegs]);

  /**
   * Connect on our own, so a rep never has to think about it.
   *
   * A phone that has to be switched on is a phone that is off when the first call arrives, and
   * "why didn't it ring" is not a question worth anyone's morning. So the console registers as
   * soon as it knows which endpoint to register — no click, and no permission prompt either:
   * mod_verto authenticates over the WebSocket, and the microphone is only asked for when a
   * call is actually placed or answered.
   *
   * It attempts exactly once per idle period, and only from `idle`. `failed` is left alone
   * because retrying an unreachable switch in a loop achieves nothing but noise; `standby`
   * because another tab holding the line is precisely a state we must not fight over. Both put
   * a button in front of the rep instead.
   */
  React.useEffect(() => {
    if (!available || registration !== "idle" || stayDisconnectedRef.current) return;
    const timer = setTimeout(() => void connect(), AUTO_CONNECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [available, registration, connect]);

  // Tear the registration down when the user signs out or their endpoint is unassigned —
  // otherwise the WebSocket outlives the session it belongs to.
  //
  // Only on the way DOWN, though. `available` is false on the first render of every page load
  // — the provider mounts while the boot refresh is still resolving `user` — so treating that
  // as "the phone went away" wiped the stored sessid before it could ever be used, and call
  // recovery silently never happened. `hadPhone` is what tells the two apart.
  const hadPhone = React.useRef(false);
  React.useEffect(() => {
    if (available) {
      hadPhone.current = true;
      return;
    }
    if (!hadPhone.current) return;
    hadPhone.current = false;
    teardown();
    setRegistration("idle");
    setCredentials(null);
    // A different person (or a re-assigned endpoint) is a fresh start: their phone should come
    // up by itself, and the previous holder's recoverable session is not theirs to re-attach.
    stayDisconnectedRef.current = false;
    clearTab(SESSID_KEY);
  }, [available, teardown]);

  React.useEffect(() => {
    mountedRef.current = true;
    // From the start, so a drop can say how long the tab had been in the background.
    watchPage();
    const onAbort = () => {
      // A keyed auth boundary unmounts us before hadPhone can observe logout. Only auth
      // cancellation erases recovery: ordinary unmounts/reloads and StrictMode must keep it.
      clearTab(SESSID_KEY);
      teardown();
      setRegistration("idle");
      setCredentials(null);
    };
    sessionSignal.addEventListener("abort", onAbort, { once: true });
    if (sessionSignal.aborted) onAbort();
    return () => {
      mountedRef.current = false;
      sessionSignal.removeEventListener("abort", onAbort);
      teardown();
    };
  }, [sessionSignal, teardown]);

  /**
   * Put the rep back in their room once the calls they handed over have gone.
   *
   * A hand-over is two things happening at the switch, in this order: the caller is moved off the
   * rep's leg — into the conference for `addToRoom`, onto the other party for a warm transfer —
   * and the rep's leg, now bridged to nobody, ends. The room leg the rep parked in order to take
   * that call is still on hold throughout, and `nextActiveCall` will hand it the speaker as soon
   * as the other legs disappear. Off hold is the one thing it cannot do for itself, and without
   * this the rep lands back in their own conference listening to hold music while the person they
   * just introduced waits.
   *
   * It waits for the legs to actually go rather than resuming at the moment of the request,
   * because until then two legs would be off hold at once — the failure every other transition
   * here is written to avoid. **All** of them, not the first: a warm transfer gives away two legs
   * and the switch ends them independently, so resuming on the first would un-hold the room while
   * the rep is still connected to the second. A hand-over the switch never carried out simply
   * leaves the marker standing until the rep hangs up by hand, which resumes the room at the same
   * point.
   */
  React.useEffect(() => {
    const handoff = handoffRef.current;
    if (!handoff || handoff.moved.some((id) => calls.some((c) => c.id === id))) return;
    clearHandoff();
    const client = clientRef.current;
    const resume = handoff.resume;
    if (!client || !resume || !heldRef.current.has(resume)) return;
    if (!calls.some((c) => c.id === resume)) return;
    setActive(resume);
    holdCall(client, resume)
      .then(syncCalls)
      .catch((e: unknown) => {
        console.error("[softphone] could not resume the room leg", e);
        setError("You are back in your room but still on hold. Press resume on the room call.");
      });
  }, [calls, clearHandoff, holdCall, setActive, syncCalls]);

  // The calls a supervisor asked to listen in on from the floor (`lib/monitor.ts`), which ring
  // this phone and must not make a sound doing it.
  const monitors = React.useSyncExternalStore(subscribeMonitors, currentMonitors, currentMonitors);
  const askingToListen = React.useSyncExternalStore(subscribeMonitors, monitorAsked, monitorAsked);
  // The ringing call the rep's campaign is answering for them (`useCampaignAutoAnswer` says).
  const campaignCall = React.useSyncExternalStore(
    subscribeCampaignAnswering, campaignAnswering, campaignAnswering,
  );

  /**
   * Make the noise the current call list calls for.
   *
   * An effect over the whole list rather than a `start()` in the `incomingCall` handler and a
   * `stop()` in three others. Every way a tone should end — answered, rejected, the far end gave
   * up, the socket dropped, the rep took the line over in another tab — arrives here as a
   * changed list, and the version that started and stopped by hand missed one of them for as
   * long as it existed: a call that ended while ringing left the phone ringing.
   *
   * `toneFor` holds the rules and is pure; this is only the wiring.
   */
  React.useEffect(() => {
    const tones = (tonesRef.current ??= new Tones());
    tones.setSink(readAudioPrefs().speakerId);
    tones.play(
      toneFor(calls, {
        withMedia,
        // The client's own map, not `calls`: `shouldAutoAnswer` refuses over any other leg, and
        // the two lists agree here because both come from the same sync.
        // A call from the number the rep dialed by hand is answered by the card that placed it
        // (`expectCall`), and is no more a thing to ring for than one this browser's auto-answer
        // takes — or than a sound test the rep has just asked for (`startSoundTest`).
        // Nor does a supervisor's listen-in ever ring (`useMonitorAutoAnswer` takes it): they
        // asked for it on the floor a moment ago. Its invite can beat the server's answer naming
        // it, so nothing rings while that answer is still on its way (`askingToMonitor`).
        // Nor does a call the rep's campaign is answering for them, or is about to
        // (`campaignTakes` in `lib/acd.ts`). Asked of the store itself and not of this render's
        // reading of it: the hook that says so runs its effect before this one in the same
        // commit, and a ring that started for the one commit in between would be heard.
        autoAnswering: (id) =>
          shouldAutoAnswer(autoAnswer || soundTestsRef.current.has(id), id, calls) ||
          (expectedNumber !== null &&
            sameNumber(calls.find((c) => c.id === id)?.remote, expectedNumber)) ||
          monitors.has(id) ||
          askingToListen ||
          campaignAnswering() === id,
      }),
    );
    // `campaignCall` is here to run this again when the campaign lets go of a call it did not
    // answer after all, which then rings.
  }, [calls, withMedia, autoAnswer, expectedNumber, monitors, askingToListen, campaignCall]);

  /**
   * The speaker the rep picks in the setup dialog, applied to what is playing right now.
   *
   * A rep opens that dialog mid-shift precisely when the sound is coming out of the wrong thing,
   * so the change has to reach the call they are on and the ringer, not only the next call. The
   * microphone is not here: moving a live call's capture device means renegotiating the media,
   * and the dialog says it applies to the next call instead.
   */
  React.useEffect(
    () =>
      subscribeAudioPrefs((prefs) => {
        audioRef.current?.setSink(prefs.speakerId);
        tonesRef.current?.setSink(prefs.speakerId);
      }),
    [],
  );

  // Give the audio hardware back when the console is closed or the user signs out. Separate from
  // `teardown`, which runs on disconnect and may be followed by another connect.
  React.useEffect(() => () => tonesRef.current?.dispose(), []);

  const primary = React.useMemo(() => {
    if (calls.length === 0) return null;
    // The active leg is the one the rep is on, so it outranks everything — a second call ringing
    // is surfaced as `waiting`, not by hijacking `primary` out from under a live conversation.
    return (
      calls.find((c) => c.id === activeCallId) ??
      calls.find((c) => c.direction === "inbound" && c.state === "ringing") ??
      calls.find((c) => c.state === "active") ??
      calls[0] ??
      null
    );
  }, [calls, activeCallId]);

  /** A second call ringing while the rep is engaged — a decision, not something to auto-answer. */
  const waiting = React.useMemo(
    () => secondaryRinging(calls, activeCallId),
    [calls, activeCallId],
  );
  const swappable = React.useMemo(
    () => swappableCalls(calls, activeCallId),
    [calls, activeCallId],
  );

  const withCall = React.useCallback(
    (callId: string | undefined) => callId ?? primary?.id ?? null,
    [primary],
  );

  const value = React.useMemo<SoftphoneValue>(
    () => ({
      available,
      registration,
      error,
      credentials,
      calls,
      primary,
      activeCallId,
      waiting,
      swappable,
      autoAnswer,
      setAutoAnswer,
      expectCall: setExpectedNumber,
      room,
      conference,
      conferenceError,
      handingOver,
      connect,
      disconnect,
      takeOver,
      createRoom,
      startSoundTest,
      soundTests,
      dial: async (destination: string) => {
        const client = clientRef.current;
        // The one place a typed destination becomes a dial string. A number arrives punctuated
        // — out of a lead record, a spreadsheet, or a document that turned the hyphens into en
        // dashes — and a SIP destination has no room for any of that. See `lib/phone-number.ts`;
        // it is here rather than in the dialler because this is the boundary, and the dialler is
        // one of four things that reaches it.
        const dest = normalizeDialString(destination);
        if (!client || !dest) return;
        // Every refusal below reports itself in the phone's own banner and then throws, so the
        // dialler knows it failed: a silent return would have it clear the number field and put
        // itself away as though the call had gone out.
        if (!canDialSecondLine(callsRef.current)) {
          setError("Wait for the call you are placing to connect before starting another.");
          throw new Error("a call is still connecting");
        }
        // Park whoever the rep is on before opening a second line — the same rule and the same
        // ordering as `answerLeg`. Unlike answering, this aborts when the hold fails: nobody is
        // waiting on the other end of a call that has not been placed yet, so the honest move is
        // to leave the rep on one call rather than put them on two live ones.
        const current = activeCallIdRef.current;
        const parked = current ? callsRef.current.find((c) => c.id === current) : undefined;
        if (parked && isEngaged(parked.state) && !heldRef.current.has(parked.id)) {
          try {
            await holdCall(client, parked.id);
            syncCalls();
          } catch (e) {
            console.error("[softphone] could not hold before dialling", e);
            setError("Could not put the current call on hold, so the new call was not placed.");
            throw e;
          }
        }
        // The id is ours so the new leg can own the speaker BEFORE `call()` emits its first
        // `callStateChange`. `nextActiveCall` keeps a live active leg, so a leg promoted after
        // the round trip would never take the audio off the call we have just parked — the rep
        // would dial out and go on hearing hold music.
        const callId = crypto.randomUUID();
        setActive(callId);
        try {
          await client.call(dest, {
            callId,
            callerIdName: credentials?.caller_name,
            callerIdNumber: credentials?.caller_number,
            audioDeviceId: (await micFor()).deviceId,
          });
        } catch (e) {
          // A refused microphone is the usual cause — the same one `answer` names, and the same
          // wording, because from the rep's side it is the same failure.
          console.error("[softphone] dial failed", e);
          setError("Could not place the call. Check that this browser may use the microphone.");
          throw e;
        } finally {
          // Also on the way out of a failure: `activeCallId` is pointing at a leg that never
          // came into being, and only a sync puts it back on a call that exists.
          syncCalls();
        }
      },
      addToRoom: async (callId: string) => {
        if (!conference) throw new Error("You are not in a conference room.");
        // The switch drops this leg the moment the caller is transferred off it, and the room
        // leg — parked in order to take this call — is still on hold. Resuming it here would
        // leave two legs off hold at once, so it is remembered and done when the leg has gone.
        //
        // Both markers go up BEFORE the round trip. The button has to go quiet while the request
        // is in flight, and the hand-off has to be recorded before the leg can possibly end: a
        // switch that acts faster than this promise resolves would otherwise take the leg away
        // while nothing was watching for it, and leave the rep on hold in their own room.
        markHandoff([callId], conference.callId);
        try {
          return await api.post<RoomParticipant>("/phone/room/participants", { call_id: callId });
        } catch (e) {
          clearHandoff();
          throw e;
        }
      },
      warmTransfer: async (fromCallId: string, toCallId: string) => {
        // Both legs go, and neither is hung up from here: the rep's leg is what keeps their
        // party alive until `uuid_bridge` has taken them, so a `hangup` racing the command
        // would end the call it was meant to transfer. The switch drops them once the bridge
        // lands (`hangup_after_bridge`, see core-api's `src/transfers.py`) and the console
        // learns it the way it learns about any call: the legs disappear.
        //
        // The parked leg is NOT taken off hold first, which looks like an omission and is not.
        // `switch_ivr_uuid_bridge` calls `switch_ivr_check_hold` on both channels before it
        // bridges them and carries the hold across itself (`CF_HOLD_ON_BRIDGE`) — resuming here
        // would only put two legs off hold at once, which is the one thing every other
        // transition in this file is written to avoid.
        //
        // The room leg is only interesting for a rep who was also sitting in their own room:
        // it is parked, and stays parked unless something takes it off hold once both of these
        // legs have gone.
        markHandoff([fromCallId, toCallId], conference?.callId ?? null);
        try {
          return await api.post<WarmTransfer>("/phone/transfers", {
            from_call_id: fromCallId,
            to_call_id: toCallId,
          });
        } catch (e) {
          clearHandoff();
          throw e;
        }
      },
      blindTransfer: async (callId: string, destination: string) => {
        // The same ending as `addToRoom`, by the same mechanism: the caller is transferred off
        // this leg and the leg — bridged to nobody — is dropped by the switch. So the marker is
        // the same one, and the rep's room (if they are in one) is resumed when the leg goes.
        markHandoff([callId], conference?.callId ?? null);
        try {
          return await api.post<BlindTransfer>("/phone/transfers/blind", {
            call_id: callId,
            // Punctuation stripped here too: a transfer target is typed by the same rep out of
            // the same lead record, and the switch dials it the same way.
            destination: normalizeDialString(destination),
          });
        } catch (e) {
          clearHandoff();
          throw e;
        }
      },
      answer: async (callId, how) => {
        const id = withCall(callId);
        const client = clientRef.current;
        if (!id || !client) return;
        if (how?.byItself) ding();
        try {
          await answerLeg(client, id, () =>
            setError(
              "Could not confirm hold. The incoming call was not answered. " +
                "Retry Hold & answer, or choose End & answer.",
            ),
          );
        } catch (e) {
          console.error("[softphone] answer failed", e);
          const message = answerFailureMessage(e);
          if (message) setError(message);
        }
      },
      hangup: async (callId) => {
        const id = withCall(callId);
        const client = clientRef.current;
        // `await undefined` resolves, so the optional-chained form made a torn-down client
        // indistinguishable from a hang-up that worked.
        if (!id || !client) return;
        await client.hangup(id);
      },
      reject: async (callId) => {
        const id = withCall(callId);
        const client = clientRef.current;
        if (!id || !client) return;
        await client.reject(id);
      },
      holdAndAnswer: async (callId) => {
        const client = clientRef.current;
        if (!client) return;
        await answerLeg(client, callId, () =>
          setError(
            "Could not confirm hold. The incoming call was not answered. " +
              "Retry Hold & answer, or choose End & answer.",
          ),
        );
      },
      endAndAnswer: async (callId) => {
        const client = clientRef.current;
        if (!client) return;
        const outgoing = activeCallIdRef.current;
        if (outgoing && outgoing !== callId) {
          // The vendored hangup closes the peer connection and stops the local tracks AFTER
          // its bye request settles (including on timeout). Wait for that isolation before
          // answering: starting hangup alone does not stop A receiving the microphone.
          await client.hangup(outgoing);
        }
        // The bye round trip can outlast the incoming call or this registration.
        if (clientRef.current !== client || client.getCall(callId)?.state !== "ringing") return;
        setError(null);
        setActive(callId);
        await answerCall(client, callId);
        syncCalls();
      },
      switchTo: async (callId) => {
        const client = clientRef.current;
        if (!client || callId === activeCallIdRef.current) return;
        const plan = swapPlan(callsRef.current, activeCallIdRef.current, callId, heldRef.current);
        // Hold before unhold, always: the moment both legs are off hold, both far ends hear the
        // rep and the browser has two streams competing for one audio element.
        if (plan.hold) {
          try {
            await holdCall(client, plan.hold);
          } catch (e) {
            // Abort rather than continue. Un-holding the target now would leave two live legs,
            // which is the failure the swap exists to prevent — and the rep would not know.
            console.error("[softphone] swap aborted: could not hold the current call", e);
            setError("Could not put the current call on hold, so the swap was cancelled.");
            return;
          }
        }
        if (plan.unhold) {
          try {
            await holdCall(client, plan.unhold);
          } catch (e) {
            console.error("[softphone] could not resume the other call", e);
            setError("Could not take the other call off hold. Try again.");
            syncCalls();
            return;
          }
        }
        setActive(callId);
        syncCalls();
      },
      toggleHold: async (callId) => {
        const id = withCall(callId);
        const client = clientRef.current;
        if (!id || !client) return;
        try {
          await holdCall(client, id);
        } catch (e) {
          // `toggleHold` can reject four ways — unknown call, dead socket, a switch-side error,
          // or its own 30-second timeout — and `heldRef` is only written once it resolves. Left
          // unhandled the button silently keeps its old state and the rejection goes nowhere but
          // the console, so a rep believes a customer is on hold when they are not.
          console.error("[softphone] hold failed", e);
          setError(
            e instanceof Error && /timeout/i.test(e.message)
              ? "The switch did not answer the hold request. The call is still live."
              : "Could not change hold on this call. The call is still live.",
          );
        }
        syncCalls();
      },
      toggleMute: async (callId) => {
        const id = withCall(callId);
        const client = clientRef.current;
        if (!id || !client) return;
        const next = !mutedRef.current.has(id);
        // THE mute. `muteMic` only sends verto.modify; the browser goes on shipping the
        // microphone until the local track is disabled, so the rep was muted on screen and
        // audible on the call.
        client.getLocalStream(id)?.getAudioTracks().forEach((t) => {
          t.enabled = !next;
        });
        if (next) mutedRef.current.add(id);
        else mutedRef.current.delete(id);
        syncCalls();
        // Server-side bookkeeping, best-effort and deliberately last: the far end is already
        // silent, so a switch that rejects this must not undo what the rep asked for.
        try {
          await (next ? client.muteMic(id) : client.unmuteMic(id));
        } catch (e) {
          console.warn("[softphone] server-side mute failed; the local track is muted anyway", e);
        }
      },
      sendDigit: async (digit, callId) => {
        const id = withCall(callId);
        const client = clientRef.current;
        if (!id || !client) return;
        await client.sendDTMF(id, digit);
      },
      memberCommand: sendMemberCommand,
    }),
    [
      available, registration, error, credentials, calls, primary, activeCallId, waiting,
      swappable, autoAnswer, setAutoAnswer, room, conference, conferenceError,
      handingOver,
      connect, disconnect, takeOver,
      createRoom, startSoundTest, soundTests, sendMemberCommand, syncCalls, withCall, holdCall,
      setActive, answerLeg, answerCall, markHandoff, clearHandoff,
    ],
  );

  return <SoftphoneContext.Provider value={value}>{children}</SoftphoneContext.Provider>;
}

export function useSoftphone(): SoftphoneValue {
  const ctx = React.useContext(SoftphoneContext);
  if (!ctx) throw new Error("useSoftphone must be used inside <SoftphoneProvider>");
  return ctx;
}

/** Ticking timer for a call's duration. Kept out of the provider so one re-render per second
 *  touches only the component showing the clock, not every consumer of the context. */
export function useCallDuration(answeredAt: number | null): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (answeredAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [answeredAt]);
  return answeredAt === null ? 0 : Math.max(0, (now - answeredAt) / 1000);
}
